import React, { useState, useCallback, useMemo } from 'react';
import { GenerateWeeklyDemand } from 'helpers/api-calls/role-calls';
import './GenerateWeeklyDemandModal.scss';

const DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const BUSINESS_TYPES = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'coffee_shop', label: 'Coffee Shop / Caf\u00e9' },
  { value: 'fast_food', label: 'Fast Food' },
  { value: 'retail_store', label: 'Retail Store' },
  { value: 'grocery_store', label: 'Grocery Store' },
  { value: 'bar_nightclub', label: 'Bar / Nightclub' },
  { value: 'warehouse', label: 'Warehouse / Distribution' },
  { value: 'call_center', label: 'Call Center' },
  { value: 'healthcare', label: 'Healthcare / Hospital' },
  { value: 'hotel', label: 'Hotel / Hospitality' },
  { value: 'other', label: 'Other' },
];

const BUSINESS_TYPE_TO_CURVE = {
  restaurant: 'realistic',
  coffee_shop: 'morning_peak',
  fast_food: 'realistic',
  retail_store: 'realistic',
  grocery_store: 'realistic',
  bar_nightclub: 'realistic',
  warehouse: 'flat',
  call_center: 'flat',
  healthcare: 'flat',
  hotel: 'flat',
  other: 'realistic',
};

const CURVE_OPTIONS = [
  {
    value: 'realistic',
    label: 'Realistic',
    description:
      'Dual-peak pattern with lunch and dinner rushes. Best for restaurants and retail.',
  },
  {
    value: 'flat',
    label: 'Flat',
    description:
      'Even staffing across all open hours. Best for warehouses and call centers.',
  },
  {
    value: 'morning_peak',
    label: 'Morning Peak',
    description:
      'Heavy morning staffing that tapers off. Best for coffee shops and breakfast spots.',
  },
];

/**
 * Generates time options in 30-minute increments from 6:00 AM to 11:30 PM.
 * Returns an array of { value, label } objects where value is the 24h hour
 * (e.g. 6, 6.5, 7, ..., 23.5) and label is the display string.
 */
function generateTimeOptions() {
  const options = [];
  for (let hour = 6; hour <= 23; hour++) {
    const ampm = hour < 12 ? 'AM' : 'PM';
    const displayHour = hour <= 12 ? hour : hour - 12;
    options.push({
      value: hour,
      label: `${displayHour}:00 ${ampm}`,
    });
    options.push({
      value: hour + 0.5,
      label: `${displayHour}:30 ${ampm}`,
    });
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

function buildDefaultHours() {
  const hours = {};
  DAYS_OF_WEEK.forEach((day) => {
    hours[day] = { start: 9, end: 21, closed: false };
  });
  return hours;
}

/**
 * Resolve the initial business type from the location's category field.
 * If the category matches a known business type value, use it; otherwise
 * default to 'restaurant'.
 */
function resolveInitialBusinessType(locationCategory) {
  if (!locationCategory) return 'restaurant';
  const normalized = locationCategory.toLowerCase().replace(/[\s/-]+/g, '_');
  const match = BUSINESS_TYPES.find((bt) => bt.value === normalized);
  return match ? match.value : 'restaurant';
}

const GenerateWeeklyDemandModal = ({
  locationId,
  locationCategory,
  onClose,
  onSuccess,
}) => {
  const initialBusinessType = useMemo(
    () => resolveInitialBusinessType(locationCategory),
    [locationCategory]
  );
  const [businessType, setBusinessType] = useState(initialBusinessType);
  const [customizeCurve, setCustomizeCurve] = useState(false);
  const [manualCurveType, setManualCurveType] = useState('realistic');
  const [hoursOfOperation, setHoursOfOperation] = useState(buildDefaultHours);
  const [sameHoursForAll, setSameHoursForAll] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // The effective curve type: manual override if customized, otherwise
  // auto-selected from business type.
  const curveType = customizeCurve
    ? manualCurveType
    : BUSINESS_TYPE_TO_CURVE[businessType] || 'realistic';

  const handleOverlayClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleBusinessTypeChange = useCallback((event) => {
    setBusinessType(event.target.value);
  }, []);

  const handleCustomizeCurveToggle = useCallback((event) => {
    const checked = event.target.checked;
    setCustomizeCurve(checked);
  }, []);

  const handleClosedChange = useCallback(
    (day, checked) => {
      setHoursOfOperation((previous) => {
        const updated = { ...previous };
        updated[day] = { ...updated[day], closed: checked };

        if (sameHoursForAll) {
          DAYS_OF_WEEK.forEach((d) => {
            updated[d] = { ...updated[d], closed: checked };
          });
        }

        return updated;
      });
    },
    [sameHoursForAll]
  );

  const handleTimeChange = useCallback(
    (day, field, value) => {
      const numericValue = parseFloat(value);
      setHoursOfOperation((previous) => {
        const updated = { ...previous };
        updated[day] = { ...updated[day], [field]: numericValue };

        if (sameHoursForAll) {
          DAYS_OF_WEEK.forEach((d) => {
            updated[d] = {
              ...updated[d],
              [field]: numericValue,
            };
          });
        }

        return updated;
      });
    },
    [sameHoursForAll]
  );

  const handleSameHoursToggle = useCallback(
    (event) => {
      const checked = event.target.checked;
      setSameHoursForAll(checked);
      if (checked) {
        setHoursOfOperation((previous) => {
          const mondayHours = previous.monday;
          const synced = {};
          DAYS_OF_WEEK.forEach((day) => {
            synced[day] = { ...mondayHours };
          });
          return synced;
        });
      }
    },
    []
  );

  const handleGenerate = useCallback(async () => {
    setErrorMessage('');
    setIsGenerating(true);

    // Build payload — convert half-hour decimals to integer hours for the API
    const hoursPayload = {};
    DAYS_OF_WEEK.forEach((day) => {
      const dayHours = hoursOfOperation[day];
      if (dayHours.closed) {
        hoursPayload[day] = { closed: true };
      } else {
        hoursPayload[day] = {
          start: dayHours.start,
          end: dayHours.end,
        };
      }
    });

    try {
      await GenerateWeeklyDemand(locationId, {
        curve_type: curveType,
        hours_of_operation: hoursPayload,
        business_type: businessType,
      });
      onSuccess();
      onClose();
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'An unexpected error occurred. Please try again.';
      setErrorMessage(message);
    } finally {
      setIsGenerating(false);
    }
  }, [locationId, curveType, businessType, hoursOfOperation, onSuccess, onClose]);

  return (
    <div
      className="generate-demand-overlay"
      onClick={handleOverlayClick}
    >
      <div className="generate-demand-modal">
        <div className="generate-demand-modal-header">
          <h3>Automatically Generate Weekly Demand</h3>
          <p>
            Configure your business type, hours of operation, and demand
            pattern, then generate a weekly demand forecast for this location.
          </p>
        </div>

        {/* Section: Business Type */}
        <div className="generate-demand-section">
          <h4>Business Type</h4>
          <select
            className="business-type-select"
            value={businessType}
            onChange={handleBusinessTypeChange}
          >
            {BUSINESS_TYPES.map((bt) => (
              <option key={bt.value} value={bt.value}>
                {bt.label}
              </option>
            ))}
          </select>
          <div className="business-type-hint">
            Demand pattern will be auto-selected based on your business type.
          </div>
        </div>

        {/* Section: Hours of Operation */}
        <div className="generate-demand-section">
          <h4>Hours of Operation</h4>

          <label className="same-hours-checkbox">
            <input
              checked={sameHoursForAll}
              type="checkbox"
              onChange={handleSameHoursToggle}
            />
            <span>Same hours for all days</span>
          </label>

          <div className="hours-table">
            <div className="hours-table-header">
              <span className="hours-col-day">Day</span>
              <span className="hours-col-closed">Closed</span>
              <span className="hours-col-time">Start</span>
              <span className="hours-col-time">End</span>
            </div>
            {DAYS_OF_WEEK.map((day) => {
              const dayHours = hoursOfOperation[day];
              const isDisabled = sameHoursForAll && day !== 'monday';

              return (
                <div
                  key={day}
                  className={`hours-table-row ${dayHours.closed ? 'closed' : ''} ${isDisabled ? 'synced' : ''}`}
                >
                  <span className="hours-col-day">{DAY_LABELS[day]}</span>
                  <span className="hours-col-closed">
                    <input
                      checked={dayHours.closed}
                      disabled={isDisabled}
                      type="checkbox"
                      onChange={(event) =>
                        handleClosedChange(day, event.target.checked)
                      }
                    />
                  </span>
                  <span className="hours-col-time">
                    <select
                      disabled={dayHours.closed || isDisabled}
                      value={dayHours.start}
                      onChange={(event) =>
                        handleTimeChange(day, 'start', event.target.value)
                      }
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </span>
                  <span className="hours-col-time">
                    <select
                      disabled={dayHours.closed || isDisabled}
                      value={dayHours.end}
                      onChange={(event) =>
                        handleTimeChange(day, 'end', event.target.value)
                      }
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section: Demand Curve Type */}
        <div className="generate-demand-section">
          <div className="curve-section-header">
            <h4>Demand Curve Type</h4>
            <span className="curve-auto-label">
              Auto-selected: <strong>{CURVE_OPTIONS.find((c) => c.value === (BUSINESS_TYPE_TO_CURVE[businessType] || 'realistic'))?.label}</strong>
            </span>
          </div>

          <label className="customize-curve-checkbox">
            <input
              checked={customizeCurve}
              type="checkbox"
              onChange={handleCustomizeCurveToggle}
            />
            <span>Customize demand pattern</span>
          </label>

          {customizeCurve && (
            <div className="curve-options">
              {CURVE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`curve-option ${manualCurveType === option.value ? 'selected' : ''}`}
                >
                  <input
                    checked={manualCurveType === option.value}
                    name="curve_type"
                    type="radio"
                    value={option.value}
                    onChange={() => setManualCurveType(option.value)}
                  />
                  <div className="curve-option-content">
                    <span className="curve-option-label">{option.label}</span>
                    <span className="curve-option-desc">
                      {option.description}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Error message */}
        {errorMessage && (
          <div className="generate-demand-error">{errorMessage}</div>
        )}

        {/* Footer */}
        <div className="generate-demand-footer">
          <button
            className="generate-demand-btn-cancel"
            disabled={isGenerating}
            type="button"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="generate-demand-btn-generate"
            disabled={isGenerating}
            type="button"
            onClick={handleGenerate}
          >
            {isGenerating ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(GenerateWeeklyDemandModal);
